// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useMemo, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Themed';
import appColors from '@/theme/colors';
import { card } from '../../typography';
import SectionHeading from '../shared/SectionHeading';
import type { RoundGroupsData } from '../../types';

type Props = {
  roundId: string;
  playerIdsWithScores?: Set<string>;
  tourMembers: Array<{ id: string; name: string }>;
  roundPlayerIds: string[];
  roundGroups?: RoundGroupsData;
  playerNameById: Record<string, string>;
  onAddRoundMember: (roundId: string, playerId: string) => void;
  onRemoveRoundMember: (roundId: string, playerId: string) => void;
  onSwapPlayers: (roundId: string, playerIdA: string, playerIdB: string) => void;
};

type InteractionMode =
  | { kind: 'idle' }
  | { kind: 'player-menu'; playerId: string }
  | { kind: 'swap-select'; sourcePlayerId: string; sourceTeamId: string };

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export default function RoundPlayersEditor({
  roundId,
  playerIdsWithScores,
  tourMembers,
  roundPlayerIds,
  roundGroups,
  playerNameById,
  onAddRoundMember,
  onRemoveRoundMember,
  onSwapPlayers,
}: Props) {
  const [interaction, setInteraction] = useState<InteractionMode>({ kind: 'idle' });

  const roundPlayerSet = useMemo(() => new Set(roundPlayerIds), [roundPlayerIds]);

  const hasScore = useCallback(
    (playerId: string) => playerIdsWithScores?.has(playerId) ?? false,
    [playerIdsWithScores]
  );

  const benchPlayers = useMemo(
    () => tourMembers.filter((m) => !roundPlayerSet.has(m.id)),
    [tourMembers, roundPlayerSet]
  );

  const handlePlayerPress = useCallback(
    (playerId: string, teamId: string) => {
      if (hasScore(playerId)) return;

      if (interaction.kind === 'swap-select') {
        if (interaction.sourcePlayerId === playerId) {
          // Cancel swap mode
          setInteraction({ kind: 'idle' });
        } else if (teamId !== interaction.sourceTeamId) {
          // Execute swap
          onSwapPlayers(roundId, interaction.sourcePlayerId, playerId);
          setInteraction({ kind: 'idle' });
        }
        return;
      }

      if (interaction.kind === 'player-menu' && interaction.playerId === playerId) {
        setInteraction({ kind: 'idle' });
        return;
      }

      setInteraction({ kind: 'player-menu', playerId });
    },
    [interaction, hasScore, onSwapPlayers, roundId]
  );

  const handleBench = useCallback(
    (playerId: string) => {
      onRemoveRoundMember(roundId, playerId);
      setInteraction({ kind: 'idle' });
    },
    [onRemoveRoundMember, roundId]
  );

  const handleStartSwap = useCallback(
    (playerId: string, teamId: string) => {
      setInteraction({ kind: 'swap-select', sourcePlayerId: playerId, sourceTeamId: teamId });
    },
    []
  );

  const handleBenchPlayerPress = useCallback(
    (playerId: string) => {
      onAddRoundMember(roundId, playerId);
    },
    [onAddRoundMember, roundId]
  );

  const getName = useCallback(
    (playerId: string) => playerNameById[playerId] ?? playerId,
    [playerNameById]
  );

  // Find which team a player belongs to (for the flat fallback)
  const findTeamId = useCallback(
    (playerId: string): string => {
      if (!roundGroups) return '';
      for (const group of roundGroups.groups) {
        for (const team of group.teams) {
          if (team.playerIds.includes(playerId)) return team.teamId;
        }
      }
      return '';
    },
    [roundGroups]
  );

  const renderPlayerCard = (playerId: string, teamId: string) => {
    const name = getName(playerId);
    const initials = getInitials(name);
    const locked = hasScore(playerId);
    const isMenuOpen = interaction.kind === 'player-menu' && interaction.playerId === playerId;
    const isSwapSource = interaction.kind === 'swap-select' && interaction.sourcePlayerId === playerId;
    const isSwapTarget = interaction.kind === 'swap-select' && interaction.sourceTeamId !== teamId && interaction.sourcePlayerId !== playerId && !locked;
    const isSameTeam = interaction.kind === 'swap-select' && interaction.sourceTeamId === teamId && interaction.sourcePlayerId !== playerId;

    return (
      <View key={playerId} style={styles.playerCardWrapper}>
        <Pressable
          style={[
            styles.playerCard,
            isSwapTarget && styles.playerCardSwapTarget,
            isSwapSource && styles.playerCardSwapSource,
            isSameTeam && styles.playerCardDimmed,
            locked && styles.playerCardLocked,
          ]}
          onPress={() => handlePlayerPress(playerId, teamId)}
        >
          <View style={[styles.avatar, isSwapSource && styles.avatarSwapSource, isSwapTarget && styles.avatarSwapTarget]}>
            <Text style={styles.avatarText}>{initials}</Text>
          </View>
          <Text style={[styles.playerName, isSwapTarget && styles.playerNameSwapTarget]} numberOfLines={1}>
            {name}
          </Text>
          {locked ? <Text style={styles.lockIcon}>{'\u{1F512}'}</Text> : null}
          {isSwapSource ? <Text style={styles.cancelIcon}>{'\u2715'}</Text> : null}
          {isSwapTarget ? <Text style={styles.swapIcon}>{'\u21C4'}</Text> : null}
        </Pressable>
        {isMenuOpen ? (
          <View style={styles.playerMenu}>
            <Pressable style={styles.menuButton} onPress={() => handleBench(playerId)}>
              <Text style={styles.menuButtonText}>Bench</Text>
            </Pressable>
            <Pressable style={styles.menuButton} onPress={() => handleStartSwap(playerId, teamId)}>
              <Text style={styles.menuButtonText}>Swap</Text>
            </Pressable>
          </View>
        ) : null}
      </View>
    );
  };

  const hasGroups = roundGroups && roundGroups.groups.length > 0;

  return (
    <View style={styles.container}>
      <SectionHeading title="Round Players" count={roundPlayerIds.length} />

      {interaction.kind === 'swap-select' ? (
        <Text style={styles.hint}>Select a player from a different team to swap with.</Text>
      ) : null}

      {hasGroups ? (
        <View style={styles.groupsGrid}>
          {roundGroups!.groups.map((group, groupIndex) => (
            <View key={group.groupId} style={styles.groupCard}>
              <View style={styles.groupHeader}>
                <Text style={styles.groupTitle}>GROUP {groupIndex + 1}</Text>
              </View>
              {group.teams.map((team, teamIndex) => (
                <View key={team.teamId} style={styles.teamSection}>
                  <Text style={styles.teamLabel}>Team {teamIndex + 1}</Text>
                  {team.playerIds.map((playerId) => renderPlayerCard(playerId, team.teamId))}
                </View>
              ))}
            </View>
          ))}
        </View>
      ) : (
        // Flat list fallback when groups haven't arrived yet
        <View style={styles.flatList}>
          {roundPlayerIds.length > 0 ? (
            roundPlayerIds.map((playerId) => {
              const teamId = findTeamId(playerId);
              return renderPlayerCard(playerId, teamId);
            })
          ) : (
            <Text style={styles.emptyText}>No players in this round yet</Text>
          )}
        </View>
      )}

      {/* Bench */}
      <View style={styles.benchSection}>
        <View style={styles.benchHeader}>
          <Text style={styles.benchTitle}>BENCH</Text>
          <View style={styles.benchBadge}>
            <Text style={styles.benchBadgeText}>{benchPlayers.length} PLAYERS</Text>
          </View>
        </View>
        <View style={styles.benchList}>
          {benchPlayers.map((player) => (
            <Pressable
              key={player.id}
              style={styles.benchPlayerCard}
              onPress={() => handleBenchPlayerPress(player.id)}
            >
              <View style={styles.avatarBench}>
                <Text style={styles.avatarText}>{getInitials(player.name)}</Text>
              </View>
              <Text style={styles.benchPlayerName} numberOfLines={1}>{player.name}</Text>
              <Text style={styles.addIcon}>+</Text>
            </Pressable>
          ))}
          {benchPlayers.length === 0 ? (
            <Text style={styles.emptyText}>All tour players are in the round</Text>
          ) : null}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    ...card,
    gap: 16,
  },
  hint: {
    fontSize: 12,
    color: appColors.accent.primary,
    fontStyle: 'italic',
    paddingHorizontal: 4,
  },
  groupsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  groupCard: {
    flex: 1,
    minWidth: 200,
    backgroundColor: appColors.surface.alt,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.default,
    overflow: 'hidden',
  },
  groupHeader: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: appColors.border.default,
  },
  groupTitle: {
    fontSize: 13,
    fontWeight: '800',
    color: appColors.text.primary,
    letterSpacing: 0.5,
  },
  teamSection: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    gap: 4,
  },
  teamLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: appColors.text.muted,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    paddingHorizontal: 4,
    marginBottom: 2,
  },
  playerCardWrapper: {
    gap: 0,
  },
  playerCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 8,
    paddingVertical: 8,
    borderRadius: 8,
  },
  playerCardSwapTarget: {
    backgroundColor: 'rgba(161,212,147,0.12)',
    borderWidth: 1,
    borderColor: appColors.accent.subtleBorder,
    borderStyle: 'dashed',
  },
  playerCardSwapSource: {
    backgroundColor: 'rgba(161,212,147,0.08)',
  },
  playerCardDimmed: {
    opacity: 0.35,
  },
  playerCardLocked: {
    opacity: 0.6,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: appColors.surface.muted,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarSwapSource: {
    backgroundColor: appColors.accent.primary,
  },
  avatarSwapTarget: {
    backgroundColor: appColors.surface.elevated,
    borderWidth: 1,
    borderColor: appColors.accent.subtleBorder,
  },
  avatarText: {
    fontSize: 12,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  playerName: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    color: appColors.text.primary,
  },
  playerNameSwapTarget: {
    color: appColors.accent.primary,
  },
  lockIcon: {
    fontSize: 14,
  },
  cancelIcon: {
    fontSize: 14,
    fontWeight: '700',
    color: appColors.status.danger,
  },
  swapIcon: {
    fontSize: 16,
    fontWeight: '700',
    color: appColors.accent.primary,
  },
  addIcon: {
    fontSize: 18,
    fontWeight: '700',
    color: appColors.accent.primary,
  },
  playerMenu: {
    flexDirection: 'row',
    gap: 6,
    paddingHorizontal: 50, // aligned under player name (avatar width + gap)
    paddingBottom: 6,
  },
  menuButton: {
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: 6,
    backgroundColor: appColors.surface.elevated,
    borderWidth: 1,
    borderColor: appColors.border.strong,
  },
  menuButtonText: {
    fontSize: 12,
    fontWeight: '700',
    color: appColors.text.secondary,
  },
  flatList: {
    gap: 4,
  },
  benchSection: {
    gap: 8,
    marginTop: 8,
  },
  benchHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  benchTitle: {
    fontSize: 13,
    fontWeight: '800',
    color: appColors.text.muted,
    letterSpacing: 0.5,
  },
  benchBadge: {
    backgroundColor: appColors.surface.elevated,
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  benchBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: appColors.text.muted,
  },
  benchList: {
    gap: 4,
  },
  benchPlayerCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 8,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  avatarBench: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: appColors.surface.muted,
    alignItems: 'center',
    justifyContent: 'center',
  },
  benchPlayerName: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    color: appColors.text.secondary,
  },
  emptyText: {
    fontSize: 14,
    color: appColors.text.muted,
    fontStyle: 'italic',
    paddingVertical: 12,
  },
});
